// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import PropTypes from 'prop-types';

// Data
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useMatch } from 'react-router-dom';

// API/MODELS
import groupAPI from "services/api/groups";
import bksAPI from "services/api/bks";
import clearwaysAPI from "modules/clearways/api";

// Map
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

// helpers
import { processGroupData } from "utils/group";
import ClearwaysSidebar from "components/Sidenavs/ClearwaysSidebar";
import { processEventsData } from "utils/event";
import { useSoftUIController } from "hooks/context";

import ClearwaysSubgroupsMap from "components/Maps/ClearwaysMaps/Subgroups";
import ClearwaysDevicesMap from "components/Maps/ClearwaysMaps/Devices";
import { enableMap } from "utils/map";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function ClearwaysSubgroupsMapView({ map, user }) {
    useEffect(() => {
        if (!map.current) return;
        enableMap(map);
    }, []);

    groupAPI.loadToken(useAuth0());
    clearwaysAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout, miniNavbar } = controller;

    const [showDevices, setShowDevices] = useState(false);
    const [hasFittedBounds, setHasFittedBounds] = useState(false);

    const { group_id } = useMatch('overview/:group_id').params;

    const set_padding = 120;
    const getMapPadding = () => ({
        left: deviceLayout === 'mobile' ? set_padding / 2 : (miniNavbar ? 0 : 450) + set_padding,
        right: deviceLayout === 'mobile' ? set_padding / 2 : set_padding,
        top: deviceLayout === 'mobile' ? set_padding / 2 : 50 + set_padding,
        bottom: deviceLayout === 'mobile' ? set_padding / 2 : set_padding
    });

    const group = useQuery({
        queryKey: ['getGroupWithID', group_id],
        queryFn: async () => {
            return await groupAPI.getGroupWithID(group_id);
        }
    });

    const devices = useQuery({
        queryKey: ['getAllDevices'],
        queryFn: async () => {
            return await bksAPI.getAllDevices();
        }
    });

    const events = useQuery({
        queryKey: ['getAllClearways'],
        queryFn: async () => {
            return await clearwaysAPI.getAllClearways();
        }
    });

    const events_data = useMemo(() => {
        var processed = events.data ? processEventsData(events.data) : [];

        return processed.filter(function (event) {
            event.groups.forEach(function (group) {
                if (group.group_id == group_id) {
                    return true;
                }
            });
            return false;
        });

    }, [events.data]);

    const groups_data = useMemo(() => {
        if (typeof group.data != 'undefined') {
            return processGroupData(group.data, devices.data);
        }

        return null;

    }, [group.data, devices.data]);

    useEffect(() => {
        if (!map.current || !group.data) return;

        var bounds = new mapboxgl.LngLatBounds();
        let hasCoords = false;

        groups_data.geojson.forEach(function (feature) {
            feature.geometry.coordinates.forEach(function (coords) {
                coords.forEach(function (coord) {
                    if (coord[0] !== 0 && coord[1] !== 0) {
                        bounds.extend(coord);
                        hasCoords = true;
                    }
                })
            })
        });

        if (hasCoords) {
            var options = { padding: getMapPadding(), linear: true, duration: 800 };
            if (hasFittedBounds) {
                options.duration = 300;
            }

            map.current.fitBounds(bounds, options);
            setHasFittedBounds(true);
        }
    }, [map, groups_data, deviceLayout]);

    useEffect(() => {
        if (!map.current || !hasFittedBounds) return;

        map.current.easeTo({
            padding: getMapPadding(),
            duration: 800,
            essential: true
        });
    }, [miniNavbar, deviceLayout]);

    if (group.isLoading || devices.isLoading || events.isLoading) {
        return 'Loading...';
    }

    if (group.error || devices.error || events.error) {
        return "An error has occurred";
    }

    if (!group.data || !devices.data) {
        return 'Loading...';
    }

    return (
        <SoftBox sx={{ width: '100%', height: '100%' }}>
            {group.data.subgroups.length > 0 ? (
                <ClearwaysSubgroupsMap {...{ map, user, groups_data, showDevices }} />
            ) : (
                <ClearwaysDevicesMap {...{ map, user, groups_data, showDevices }} />
            )}
            <ClearwaysSidebar
                user={user}
                groupPreviewID={group_id}
                events={events_data}
                showDevices={showDevices}
                setShowDevices={setShowDevices}
            />
        </SoftBox>
    )
}

ClearwaysSubgroupsMapView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
}

export default ClearwaysSubgroupsMapView;